import { ArticlesOfAssociation } from "./ArticlesOfAssociation";
import { RoleType } from "./enums/RoleType";
import { BR_STATUS, EnterpriseAccountFeature, PhoneNumber, Whitelabel } from "@/common/common";
import { Language } from "@/i18n/languages";
import { TMessageKey } from "@/i18n/types.gen";
import { ProductSubscriptionResponse } from "@/payment/Payment";
import { CompanyDocumentSettings } from "./Settings";
import { ShareType } from "@/common/share";
import { FeatureToggles } from "@/common/featureToggles";
import { OwnershipModel } from "@/companyRegistry/companyRegistryApiClient";
import type { CompanyStatusChangeDTO } from "./Transactions";
import type { AccountRoleType } from "@/companyRoles/accountRoleTypeApiClient";
import type { RolePeriod } from "@/companyRoles/rolePeriodsDomain.ts";

export type CompanyroleId = number;

export interface CompanyRole {
  companyroleId: CompanyroleId;
  role: RoleType;
  accountRoleType?: AccountRoleType;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  userId?: number;
  socialSecurityNumber?: string;
  samePersonRoleIds?: number[];
  language?: Language;
  lastLogin?: string;
  birthDate?: string;
  brStatus?: BR_STATUS;
  projectId?: string;
  projectName?: string;
  projectDate?: string;
  nationality?: string;
  dNumber?: string;
  validFrom?: string;
  validTo?: string;
  residentialAddress?: ResidentialAddress;
  periods?: RolePeriod[];
}

export interface ResidentialAddress {
  addressLine1?: string;
  addressLine2?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

export interface PersonRolleGrunnlag {
  fodselsdato?: string;
  navn?: string;
  rolle?: {
    kode?: string;
    tekstforklaring?: string;
  };
}

export interface SignaturesAndProcura {
  signeringsGrunnlag: {
    kode: string;
    tekstforklaring: string;
    signaturProkuraRoller: {
      signaturProkuraFritekst?: string;
      personRolleGrunnlag?: PersonRolleGrunnlag[];
    };
    muligeSigneringsRoller: {
      personRolleGrunnlag: PersonRolleGrunnlag[];
    };
  };

  signeringsKombinasjon?: {
    kombinasjon?: Array<{
      tekstforklaring?: string;
      personRolleKombinasjon?: PersonRolleGrunnlag[];
    }>;
  };
}

export enum PowerOfAttorneyType {
  CAPITAL_INCREASE = "CAPITAL_INCREASE",
  DIVIDEND = "DIVIDEND",
  GROUP_CONTRIBUTION = "GROUP_CONTRIBUTION",
  ACQUIRE_OWN_SHARES = "ACQUIRE_OWN_SHARES",
  PLEDGE_OWN_SHARES = "PLEDGE_OWN_SHARES",
  CONVERTIBLE_LOAN = "CONVERTIBLE_LOAN",
}

export const poaTypeLabels: { [key in PowerOfAttorneyType]: TMessageKey } = {
  [PowerOfAttorneyType.CAPITAL_INCREASE]: "poa.capitalIncrease",
  [PowerOfAttorneyType.DIVIDEND]: "poa.dividend",
  [PowerOfAttorneyType.GROUP_CONTRIBUTION]: "poa.group_contribution",
  [PowerOfAttorneyType.ACQUIRE_OWN_SHARES]: "poa.acquireOwnShares",
  [PowerOfAttorneyType.PLEDGE_OWN_SHARES]: "poa.pledgeOwnShares",
  [PowerOfAttorneyType.CONVERTIBLE_LOAN]: "poa.convertibleLoan",
};

export interface PowerOfAttorney {
  id: string;
  dateGiven: string;
  expiryDate?: string;
  amount?: number;
  type: PowerOfAttorneyType;
  dateRegistered?: string;
  currency: string;
  hasExpired: boolean;
  isRenewalPoaCandidate: boolean;
  dismissedRenewalCandidate: boolean;
}

export interface CompanyInfo {
  company: Company;
  auditorName: string;
  articles?: ArticlesOfAssociation;
  shareNominalValue: number;
  shareTypes: ShareType[];
  shareCapital: number;
  totalShares: number;
  numberOfSharesPerShareholder: { [key: number]: number };
  roles: CompanyRole[];
  powerOfAttorneys: PowerOfAttorney[];
  signatureInformation: SignaturesAndProcura;
  prokuraInformation?: SignaturesAndProcura;
  acceptedTerms: boolean;
  forceReacceptTerms: boolean;
  numberOfSharesWithMissingOwner: number;
  shareNominalValueConfirmed: boolean;
  existingSubscription?: ProductSubscriptionResponse;
  documentSettings?: CompanyDocumentSettings;
  remark?: string;
  followUpCount?: number;
  enterpriseAccountInfo?: EnterpriseAccountInfo;
  featureToggles: FeatureToggles;
  liquidatedTransactionDate?: string;
  liquidatedTransactionId?: number;
  mergedTransactionDate?: string;
  mergedTransactionId?: number;
  mergedAcquirorName?: string;
  companyStatusChange?: CompanyStatusChangeDTO;
  hasAutomaticShareStructure?: boolean;
  totalPaidInShareCapital?: number;
  totalPaidInSharePremium?: number;
  showNationalId?: boolean;
  ownershipModel?: OwnershipModel;
}

export enum CompanyStatus {
  ACTIVE = "ACTIVE",
  UNDER_INCORPORATION = "UNDER_INCORPORATION",
  SHELVED = "SHELVED",
  IN_LIQUIDATION = "IN_LIQUIDATION",
  LIQUIDATED = "LIQUIDATED",
  DIVESTED = "DIVESTED",
  MERGED = "MERGED",
}

export const companyStatusLabels: { [key in CompanyStatus]: TMessageKey } = {
  [CompanyStatus.ACTIVE]: "settings.company-status.ACTIVE",
  [CompanyStatus.UNDER_INCORPORATION]: "settings.company-status.UNDER_INCORPORATION",
  [CompanyStatus.SHELVED]: "settings.company-status.SHELVED",
  [CompanyStatus.IN_LIQUIDATION]: "settings.company-status.IN_LIQUIDATION",
  [CompanyStatus.LIQUIDATED]: "settings.company-status.LIQUIDATED",
  [CompanyStatus.DIVESTED]: "settings.company-status.DIVESTED",
  [CompanyStatus.MERGED]: "settings.company-status.MERGED",
};

export const ownershipEndStatuses = [
  CompanyStatus.DIVESTED,
  CompanyStatus.LIQUIDATED,
  CompanyStatus.MERGED,
];

export type CompanyStatusDates = {
  dissolutionRegisteredDate?: string;
  creditorNoticeExpiryDate?: string;
};

export const appBarCompanyStatusLabels: {
  [key in CompanyStatus]: TMessageKey | undefined;
} = {
  [CompanyStatus.ACTIVE]: undefined,
  [CompanyStatus.UNDER_INCORPORATION]: "app-bar.company-status.UNDER_INCORPORATION",
  [CompanyStatus.SHELVED]: "app-bar.company-status.SHELVED",
  [CompanyStatus.IN_LIQUIDATION]: "app-bar.company-status.IN_LIQUIDATION",
  [CompanyStatus.LIQUIDATED]: "app-bar.company-status.LIQUIDATED",
  [CompanyStatus.DIVESTED]: "app-bar.company-status.DIVESTED",
  [CompanyStatus.MERGED]: "app-bar.company-status.MERGED",
};

export const appBarCompanyStatusSinceLabels: {
  [key in CompanyStatus]: TMessageKey | undefined;
} = {
  [CompanyStatus.ACTIVE]: undefined,
  [CompanyStatus.UNDER_INCORPORATION]: undefined,
  [CompanyStatus.SHELVED]: "app-bar.company-status-since.SHELVED",
  [CompanyStatus.IN_LIQUIDATION]: "app-bar.company-status-since.IN_LIQUIDATION",
  [CompanyStatus.LIQUIDATED]: "app-bar.company-status-since.LIQUIDATED",
  [CompanyStatus.DIVESTED]: "app-bar.company-status-since.DIVESTED",
  [CompanyStatus.MERGED]: "app-bar.company-status-since.MERGED",
};

export interface Company {
  auditorCompanyOrganizationnumber?: string;
  accountantOrganizationnumber?: string;
  business_address1?: string;
  business_city?: string;
  business_country?: string;
  business_zip?: string;
  businessAddress1: string;
  businessAddress2: string;
  businessCity: string;
  businessCountry: string;
  businessZip: string;
  created: string;
  mailingAddress1: string;
  mailingAddress2: string;
  mailingCity: string;
  mailingCountry: string;
  mailingZip: string;
  naeringskode1: string;
  name: string;
  organizationnumber: string;
  companyId: string;
  affiliateId?: string;
  currency: string;
  country: string;
  confirmedAutoImport: boolean;
  status: CompanyStatus;
  whitelabel?: Whitelabel;
  enterpriseAccountId?: string;
  brAoADate?: string;
  organisationType?: string;
  registrationSubdivision?: string;
  tinNumber?: string;
}

export interface EnterpriseAccountInfo {
  enterpriseAccountId: string;
  accountName: string;
  contactName?: string;
  contactEmail?: string;
  features: EnterpriseAccountFeature[];
  isAccountAdmin: boolean;
}

export interface SuggestedCompany {
  name: string;
  organizationnumber: string;
  address?: string;
  address2?: string;
  zip?: string;
  city?: string;
  sourceCountry?: string;
}
